import {
  faCopy,
  faEnvelope,
  faPaperclip,
  faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { formatFileSize } from '../../../../utils/format-file-size';
import { surfaceHeaderProps } from '../SurfaceHeader';

/** A generated file that rides the email when the user hits Send. */
export interface EmailAttachment {
  readonly artifactId: string;
  readonly fileName: string;
  readonly sizeBytes?: number;
}

export interface EmailComposerProps {
  readonly to?: string;
  readonly cc?: string;
  readonly bcc?: string;
  readonly subject?: string;
  readonly body?: string;
  readonly attachments?: readonly EmailAttachment[];
  readonly disabled?: boolean;
  readonly onToChange?: (value: string) => void;
  readonly onCcChange?: (value: string) => void;
  readonly onBccChange?: (value: string) => void;
  readonly onSubjectChange?: (value: string) => void;
  readonly onBodyChange?: (value: string) => void;
  readonly onSend?: () => void;
  readonly onCopy?: () => void;
}

export function EmailComposer({
  to = '',
  cc = '',
  bcc = '',
  subject = '',
  body = '',
  attachments = [],
  disabled = false,
  onToChange,
  onCcChange,
  onBccChange,
  onSubjectChange,
  onBodyChange,
  onSend,
  onCopy,
}: EmailComposerProps) {
  return (
    <Paper
      radius="md"
      withBorder
      p="lg"
      style={{
        borderColor: 'var(--mantine-color-blue-2)',
        width: '100%',
      }}
    >
      <Group justify="space-between" {...surfaceHeaderProps('lg')}>
        <Group gap="xs">
          <FontAwesomeIcon
            icon={faEnvelope}
            style={{
              color: 'var(--mantine-color-blue-6)',
              width: 18,
              height: 18,
            }}
          />
          <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
            EMAIL DRAFT
          </Text>
        </Group>
        <Group gap="xs">
          <Button
            variant="outline"
            color="gray"
            size="sm"
            disabled={disabled}
            leftSection={
              <FontAwesomeIcon
                icon={faCopy}
                style={{ width: 13, height: 13 }}
              />
            }
            onClick={onCopy}
          >
            Copy
          </Button>
          <Button
            variant="outline"
            color="blue"
            size="sm"
            disabled={disabled}
            leftSection={
              <FontAwesomeIcon
                icon={faPaperPlane}
                style={{ width: 13, height: 13 }}
              />
            }
            onClick={onSend}
          >
            Send
          </Button>
        </Group>
      </Group>

      <Stack gap={6} mb="md">
        <TextInput
          label="Subject"
          value={subject}
          disabled={disabled}
          onChange={(e) => onSubjectChange?.(e.currentTarget.value)}
        />
        <TextInput
          label="To"
          value={to}
          disabled={disabled}
          onChange={(e) => onToChange?.(e.currentTarget.value)}
        />
        <TextInput
          label="Cc"
          value={cc}
          disabled={disabled}
          placeholder="Comma-separated addresses"
          onChange={(e) => onCcChange?.(e.currentTarget.value)}
        />
        <TextInput
          label="Bcc"
          value={bcc}
          disabled={disabled}
          placeholder="Comma-separated addresses"
          onChange={(e) => onBccChange?.(e.currentTarget.value)}
        />
      </Stack>

      <Divider mb="md" />

      <Textarea
        autosize
        minRows={6}
        value={body}
        disabled={disabled}
        onChange={(e) => onBodyChange?.(e.currentTarget.value)}
      />

      {attachments.length > 0 && (
        <Group gap="xs" mt="md">
          {attachments.map((attachment) => {
            const size = formatFileSize(attachment.sizeBytes);
            return (
              <Paper
                key={attachment.artifactId}
                withBorder
                radius="sm"
                px="sm"
                py={6}
              >
                <Group gap={6} wrap="nowrap">
                  <FontAwesomeIcon
                    icon={faPaperclip}
                    style={{ width: 12, height: 12 }}
                  />
                  <Text fz={13} fw={500}>
                    {attachment.fileName}
                  </Text>
                  {size && (
                    <Text fz={12} c="dimmed">
                      {size}
                    </Text>
                  )}
                </Group>
              </Paper>
            );
          })}
        </Group>
      )}
    </Paper>
  );
}
